/**
 * Entorno del navegador relevante para un lector en modo teclado. Se adjunta
 * al registro de diagnóstico de cada lectura del pickeo.
 *
 * Lo más valioso es la muestra de la distribución de teclado del host
 * (`navigator.keyboard.getLayoutMap()`, disponible solo en Chromium de
 * escritorio): el Zebra emula un teclado US, así que si el host está en
 * español el `Minus` físico llega como `'` y el `Slash` como `-`. Con el mapa
 * se ve de antemano qué caracteres van a llegar cambiados en esa PC.
 * Se calcula una vez por sesión; si algo falla se manda todo en null.
 */

import type { EntornoLector } from '@/api/endpoints/pickeo-log';

/** Teclas físicas cuyo carácter cambia entre distribuciones US / ES / LatAm. */
const TECLAS_MUESTRA = [
  'Minus',
  'Equal',
  'BracketLeft',
  'BracketRight',
  'Semicolon',
  'Quote',
  'Backslash',
  'Backquote',
  'IntlBackslash',
  'Comma',
  'Period',
  'Slash',
  'Digit7',
  'Digit8',
  'Digit9',
  'Digit0',
];

/** `navigator.keyboard` no está en lib.dom: tipado mínimo y defensivo. */
interface MapaLayout {
  get(code: string): string | undefined;
}
interface NavigatorConTeclado extends Navigator {
  keyboard?: { getLayoutMap?: () => Promise<MapaLayout> };
}

const SIN_ENTORNO: EntornoLector = { plataforma: null, idioma: null, layout: null };

let cache: Promise<EntornoLector> | null = null;

export function obtenerEntornoLector(): Promise<EntornoLector> {
  if (!cache) cache = leerEntorno().catch(() => SIN_ENTORNO);
  return cache;
}

async function leerEntorno(): Promise<EntornoLector> {
  if (typeof navigator === 'undefined') return SIN_ENTORNO;
  const nav = navigator as NavigatorConTeclado;
  const plataforma = nav.platform || null;
  const idioma = nav.language || null;
  let layout: Record<string, string> | null = null;
  const teclado = nav.keyboard;
  if (teclado && typeof teclado.getLayoutMap === 'function') {
    const mapa = await teclado.getLayoutMap();
    layout = {};
    for (const code of TECLAS_MUESTRA) {
      const ch = mapa.get(code);
      if (ch != null) layout[code] = ch;
    }
  }
  return { plataforma, idioma, layout };
}
